// /home/szymon/kreator2/frontend/src/components/editor/EditorState.js
// v10.1.0 – Dodano loadingPages + usunięto zbędne logi.

import { useState, useEffect, useRef } from "react";
import { saveProjectToDb } from "../../services/projectApi";
// Importujemy nową funkcję pomocniczą
import { calculateCenteredX } from "./snapUtils";

// Usunięto funkcję autoSnapAllPagesWithSimulation - wraca do Editor.js


// Uniwersalna funkcja do zapisu w DB (admin / client).
async function handleSaveToDb({
  projectId,
  downloadKey,
  jsonData,
  saveType,
  backendUrl,
  isPublicMode,
  publicMethodUsed // "key" / "id" / null => decyduje ścieżkę w trybie public
}) {
  // Minimalny log, do debug ewentualnego
  // console.log("[EditorState] handleSaveToDb =>", { projectId, downloadKey, saveType });

  let url;
  if (!isPublicMode) {
    // tryb admin
    url = `${backendUrl}/admin/projects/${projectId}/history`;
  } else {
    // tryb public
    if (publicMethodUsed === "key") {
      url = `${backendUrl}/client/projects-key/${downloadKey}/history`;
    } else {
      url = `${backendUrl}/client/projects/${projectId}/history`;
    }
  }

  return saveProjectToDb({ url, jsonData, saveType });
}

/**
 * parseOnePage:
 *  - baseWidth/baseHeight -> fallback: pageWidth/pageHeight -> fallback: 800×600
 */
function parseOnePage(pageData) {
  let w = pageData.baseWidth;
  let h = pageData.baseHeight;

  if (typeof w !== "number" || typeof h !== "number") {
    w = pageData.pageWidth;
    h = pageData.pageHeight;
  }
  if (typeof w !== "number" || isNaN(w)) w = 800;
  if (typeof h !== "number" || isNaN(h)) h = 600;

  // Process items array if it exists (for templates)
  const texts = [];
  const images = [];
  const svgs = [];
  if (Array.isArray(pageData.items)) {
    pageData.items.forEach(item => {
      // Convert ID to string for Konva compatibility
      // Extract x, y from bbox
      const x = item.bbox ? item.bbox[0] : 0;
      const y = item.bbox ? item.bbox[1] : 0;
      // --- ADD LOGGING ---
      console.log(`[parseOnePage] Item ID: ${item.id}, Type: ${item.type}, BBox: ${JSON.stringify(item.bbox)}, Extracted X: ${x}, Extracted Y: ${y}`);
      // --- END LOGGING ---
      const stringIdItem = { ...item, id: String(item.id), x: x, y: y };

      if (item.type === 'text') {
        texts.push(stringIdItem);
      } else if (item.type === 'image') {
        // Ensure imagePath is converted to src if necessary
        // Use width/height from item if available, otherwise calculate from bbox (less common for images here)
        const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
        const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        images.push({ ...stringIdItem, src: item.imagePath || item.src, width, height });
      } else if (item.type === 'svg') { // Assuming SVG type is 'svg'
         const width = item.width || (item.bbox ? item.bbox[2] - item.bbox[0] : undefined);
         const height = item.height || (item.bbox ? item.bbox[3] - item.bbox[1] : undefined);
        svgs.push({ ...stringIdItem, src: item.svgPath || item.src, width, height }); // Adjust property name if needed
      }
    });
  } else {
    // Fallback to direct arrays (for projects/orders) - ensure IDs are strings and x/y are present
    (pageData.texts || []).forEach(item => texts.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.images || []).forEach(item => images.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
    (pageData.svgs || []).forEach(item => svgs.push({ ...item, id: String(item.id), x: item.x ?? 0, y: item.y ?? 0 }));
  }
  // Removed duplicated fallback block and extra closing brace

  return {
    baseWidth: w,
    baseHeight: h,
    texts: texts,
    images: images,
    svgs: svgs,
    guides: pageData.guides || [],
    personalizacja: pageData.personalizacja || [],
    pageIndex: pageData.pageIndex ?? 0
  };
}

export function useEditorState({
  backendUrl = "http://localhost:6001",
  pdfData = null,
  projectId = null, // Może być ID projektu lub ID pliku szablonu
  downloadKey = null,
  isAccepted = false,
  isTemplateEditor = false // Nowa flaga wskazująca, czy edytujemy szablon
}) {
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [publicMode, setPublicMode] = useState(false);
  const [publicMethodUsed, setPublicMethodUsed] = useState(null); // "key"|"id"|null

  const [fontsAreLoaded, setFontsAreLoaded] = useState(false);
  const [availableFonts, setAvailableFonts] = useState([]);

  const [assetsAreLoaded, setAssetsAreLoaded] = useState(false);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsCount, setAssetsCount] = useState(0);

  const [selectedShapeId, setSelectedShapeId] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);
  const [panelMode, setPanelMode] = useState(null);
  const [editingTextId, setEditingTextId] = useState(null);

  const [forceRefresh, setForceRefresh] = useState(0);
  const changesCountRef = useRef(0);

  const [hasLoadedFromDb, setHasLoadedFromDb] = useState(false);
  const [loadingPages, setLoadingPages] = useState(false); // do spinnera
  const [initialSetupDone, setInitialSetupDone] = useState(false); // NOWY STAN

  const initialCenteringAppliedRef = useRef(false); // Nowy ref do śledzenia centrowania

  // ============ Auto-zapis =============
  function doAutoSave(customPages) {
    if (publicMode && isAccepted) return; // brak zapisu w public mode po akceptacji

    const usedPages = customPages || pages;
    const final = usedPages.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "autosave",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed
    }).catch(() => {});
    changesCountRef.current = 0;
  }

  // ============ Ręczny zapis =============
  function handleManualSave(customPages) {
    if (publicMode && isAccepted) return;

    const usedPages = customPages || pages;
    const final = usedPages.map((pg, idx) => ({
      ...pg,
      pageIndex: pg.pageIndex ?? idx
    }));
    const finalJson = { pages: final };
    if (downloadKey) finalJson.downloadKey = downloadKey;

    handleSaveToDb({
      projectId,
      downloadKey,
      jsonData: finalJson,
      saveType: "manual",
      backendUrl,
      isPublicMode: publicMode,
      publicMethodUsed
    }).catch(() => {});
  }

  // 1) Set initial state from pdfData (runs before DB load finishes)
  useEffect(() => {
    // This effect runs whenever pdfData changes OR initially if pdfData starts as null.
    // We only process pdfData if it's actually provided.
    // The main goal here is to set initialSetupDone = true eventually.

    if (hasLoadedFromDb) {
        console.log("[EditorState] Skipping pdfData processing because DB data is already loaded.");
        // Still ensure initialSetupDone is true if we somehow get here later
        if (!initialSetupDone) setInitialSetupDone(true);
        return;
    }

    let processedPdfData = false;
    if (pdfData) { // Only process if pdfData is not null/undefined
        if (pdfData?.publicMode) setPublicMode(true);

        let initialPagesData = null;
        if (Array.isArray(pdfData?.pdfMetadatas) && pdfData.pdfMetadatas.length > 0 && pdfData.pdfMetadatas[0]?.metadata?.pages) {
            console.log("[EditorState] Detected template structure in pdfData.");
            initialPagesData = pdfData.pdfMetadatas[0].metadata.pages;
        } else if (Array.isArray(pdfData?.pages) && pdfData.pages.length > 0) {
            console.log("[EditorState] Detected direct pages structure in pdfData.");
            initialPagesData = pdfData.pages;
        }

        if (initialPagesData) {
          const newPages = initialPagesData.map((pg, i) => {
            if (pg.pageIndex === undefined) pg.pageIndex = i;
            return parseOnePage(pg);
          });
          console.log("[EditorState] Setting initial pages state from provided pdfData.");
          setPages(newPages);
          processedPdfData = true;
        } else {
            console.log("[EditorState] No valid pages data found in provided pdfData.");
        }
    } else {
         console.log("[EditorState] Initial pdfData is null or undefined.");
    }

    // Crucially, set initialSetupDone = true regardless of whether pdfData was processed,
    // allowing the history loading effect to run.
    if (!initialSetupDone) {
        console.log("[EditorState] Setting initialSetupDone=true.");
        setInitialSetupDone(true);
    }

  }, [pdfData]); // Dependency remains pdfData

  // 2) ładowanie z DB (admin/client) - Powinno się uruchomić ponownie, gdy zmienią się zależności (np. publicMode)
  useEffect(() => {
    // Dodano warunek: Nie uruchamiaj, dopóki pdfData nie zostanie załadowane i przetworzone ORAZ initialSetupDone nie jest true
    if (!initialSetupDone) {
        console.log("[EditorState] History load useEffect skipped: initialSetupDone is false.");
        return;
    }
    // Usunięto warunek !pdfData, bo initialSetupDone go zastępuje
    // Usunięto didRunRef.current, aby umożliwić ponowne uruchomienie po zmianie publicMode

    // *** NOWOŚĆ: Nie ładuj historii dla edytora szablonów ***
    if (isTemplateEditor) {
        console.log("[EditorState] History load useEffect skipped: isTemplateEditor is true.");
        setLoadingPages(false); // Upewnij się, że ładowanie jest zakończone
        return;
    }
    // *** KONIEC NOWOŚCI ***

    // Sprawdzamy flagę, która zapobiega ponownemu ładowaniu projektu z serwera
    if (window.preventProjectReload) {
      console.log("[EditorState] Pomijam ładowanie projektu z serwera, ponieważ window.preventProjectReload = true");
      setLoadingPages(false);
      return;
    }

    // Clean identifiers just in case
    const cleanedProjectId = projectId ? String(projectId).replace(/[^0-9]/g, '') : null;
    const cleanedDownloadKey = downloadKey ? String(downloadKey).replace(/[^a-zA-Z0-9]/g, '') : null;

    if (!cleanedProjectId && !cleanedDownloadKey) {
        console.log("[EditorState] No valid projectId or downloadKey provided for loading.");
        return; // Exit if no valid identifiers
    }

    setLoadingPages(true);

    (async () => {
      let done = false;
      let loadedData = null; // Store loaded data here

      // a) Try loading via downloadKey
      if (publicMode && cleanedDownloadKey && !done) {
        const urlKey = `${backendUrl}/client/projects-key/${cleanedDownloadKey}/history/latest`;
        console.log(`[EditorState] Attempting load via key: ${urlKey}`);
        try {
          const r1 = await fetch(urlKey, { credentials: "include" });
          if (r1.ok) {
            const d1 = await r1.json();
            if (Array.isArray(d1.jsonData?.pages)) {
              loadedData = d1.jsonData; // Store data
              setPublicMethodUsed("key");
              done = true;
              console.log("[EditorState] Load via key successful.");
            } else {
               console.log("[EditorState] Load via key OK, but no pages array found in jsonData.");
            }
          } else {
             console.log(`[EditorState] Load via key failed, status: ${r1.status}`);
          }
        } catch (err) {
           console.error("[EditorState] Load via key fetch error:", err);
        }
      }

      // b) Try loading via projectId (if key failed or not applicable)
      if (!done && publicMode && cleanedProjectId) {
        const urlPublicId = `${backendUrl}/client/projects/${cleanedProjectId}/history/latest`;
        console.log(`[EditorState] Attempting load via public id: ${urlPublicId}`);
        try {
          const r2 = await fetch(urlPublicId, { credentials: "include" });
          if (r2.ok) {
            const d2 = await r2.json();
            if (Array.isArray(d2.jsonData?.pages)) {
              loadedData = d2.jsonData; // Store data
              setPublicMethodUsed("id");
              done = true;
              console.log("[EditorState] Load via public id successful.");
            } else {
               console.log("[EditorState] Load via public id OK, but no pages array found in jsonData.");
            }
          } else {
             console.log(`[EditorState] Load via public id failed, status: ${r2.status}`);
          }
        } catch (err) {
           console.error("[EditorState] Load via public id fetch error:", err);
        }
      }

      // c) Try loading via admin projectId (if others failed)
      if (!done && !publicMode && cleanedProjectId) {
         const urlAdmin = `${backendUrl}/admin/projects/${cleanedProjectId}/history/latest`;
         console.log(`[EditorState] Attempting load via admin id: ${urlAdmin}`);
         try {
           const r3 = await fetch(urlAdmin, { credentials: "include" });
           if (r3.ok) {
             const d3 = await r3.json();
             if (Array.isArray(d3.jsonData?.pages)) {
               loadedData = d3.jsonData; // Store data
               setPublicMethodUsed(null); // Admin mode doesn't use publicMethodUsed
               done = true;
               console.log("[EditorState] Load via admin id successful.");
             } else {
                console.log("[EditorState] Load via admin id OK, but no pages array found in jsonData.");
             }
           } else {
              console.log(`[EditorState] Load via admin id failed, status: ${r3.status}`);
           }
         } catch (err) {
            console.error("[EditorState] Load via admin id fetch error:", err);
         }
      }

      // Logowanie wyniku ładowania danych PRZED sprawdzeniem `if (loadedData)`
      console.log("[EditorState] Fetching history completed.");
      console.log("[EditorState] loadedData:", loadedData);
      console.log("[EditorState] publicMethodUsed:", publicMethodUsed);
      console.log("[EditorState] done flag:", done);


      // Now, if data was successfully loaded from history, update the state
      if (loadedData) {
        // Jeśli dane z DB się załadowały, po prostu je ustawiamy.
        // Centrowanie zostanie wykonane w osobnym useEffect poniżej.
        const parsedPagesFromDb = loadedData.pages.map(parseOnePage);
        console.log(`[EditorState] Loaded from DB (${publicMethodUsed || 'admin'}), setting pages from history.`);
        setPages(parsedPagesFromDb);
        setHasLoadedFromDb(true); // Mark as loaded from DB
      } else {
        console.log("[EditorState] Failed to load from DB history. Falling back to initial pdfData.");
        // Explicitly use pdfData if DB load failed and initial data exists
        let fallbackPagesData = null;
        if (Array.isArray(pdfData?.pdfMetadatas) && pdfData.pdfMetadatas.length > 0 && pdfData.pdfMetadatas[0]?.metadata?.pages) {
            fallbackPagesData = pdfData.pdfMetadatas[0].metadata.pages;
        } else if (Array.isArray(pdfData?.pages) && pdfData.pages.length > 0) {
            fallbackPagesData = pdfData.pages;
        }

        if (!hasLoadedFromDb && fallbackPagesData) {
          const initialPages = fallbackPagesData.map(parseOnePage);
          console.log("[EditorState] Setting pages state from initial pdfData as fallback.");
          setPages(initialPages);
        } else {
            console.log("[EditorState] No valid fallback pages data found in pdfData.");
        }
        // Ensure publicMethodUsed is set correctly for subsequent saves even if history load failed
        if (publicMode) {
           if (cleanedDownloadKey) setPublicMethodUsed("key");
           else if (cleanedProjectId) setPublicMethodUsed("id");
        }
      }

      setLoadingPages(false);
    })();
  }, [projectId, downloadKey, backendUrl, publicMode, initialSetupDone]); // Dodano initialSetupDone do zależności


  // 3) --- USUNIĘTO EFEKT JEDNORAZOWEGO CENTROWANIA ---
  // Logika centrowania jest teraz obsługiwana w useEditorLocalCallbacks -> handleChangeTextLocal,
  // wyzwalana przez EditableText po zmierzeniu wymiarów.


  // 4) fonty (zmieniono numerację komentarza)
  useEffect(() => {
    let isMounted = true;
    (async function loadAllFonts() {
      try {
        const fontsUrl = publicMode
          ? `${backendUrl}/client/fonts`
          : `${backendUrl}/admin/fonts`;
        const resp = await fetch(fontsUrl, { credentials: "include" });
        if (!resp.ok) throw new Error("font error");
        const list = await resp.json();
        const prefixRaw = publicMode
          ? `${backendUrl}/client/raw-file?path=`
          : `${backendUrl}/admin/raw-file?path=`;

        const tasks = list.map(async (f) => {
          let pathF = f.filePath || "";
          const prefix = "/home/szymon/kreator2/backend/uploads/";
          if (pathF.startsWith(prefix)) {
            pathF = pathF.slice(prefix.length);
          }
          if (!pathF.includes("/")) {
            pathF = "fonts/" + pathF;
          }
          pathF = pathF.replace(/[()]/g, "").replace(/\s/g, "");
          const finalUrl = prefixRaw + encodeURIComponent(pathF);

          try {
            const ff = new FontFace(f.name, `url(${finalUrl})`);
            await ff.load();
            document.fonts.add(ff);
          } catch {}
        });
        await Promise.allSettled(tasks);
        if (isMounted) {
          setAvailableFonts(list);
          setFontsAreLoaded(true);
        }
      } catch {
        if (isMounted) setFontsAreLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [backendUrl, publicMode]);

  // useEffect(() => { // Ten useEffect może nie być już potrzebny, jeśli forceRefresh jest wyzwalany inaczej
  //   if (fontsAreLoaded) setForceRefresh((p) => p + 1);
  // }, [fontsAreLoaded]);

  // 5) assety (zmieniono numerację komentarza)
  useEffect(() => {
    if (pages.length === 0) {
      setAssetsAreLoaded(true);
      return;
    }
    let total = 0;
    let loaded = 0;
    pages.forEach((pg) => {
      total += (pg.images || []).length;
      total += (pg.svgs || []).length;
    });
    if (total === 0) {
      setAssetsAreLoaded(true);
      setAssetsCount(0);
      setAssetsTotal(0);
      return;
    }
    setAssetsTotal(total);
    setAssetsCount(0);

    pages.forEach((pg) => {
      (pg.images || []).forEach((img) => {
        const iObj = new Image();
        iObj.crossOrigin = "anonymous";
        iObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        iObj.src = img.src || "";
      });
      (pg.svgs || []).forEach((sv) => {
        const sObj = new Image();
        sObj.crossOrigin = "anonymous";
        sObj.onload = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.onerror = () => {
          loaded++;
          setAssetsCount(loaded);
          if (loaded >= total) setAssetsAreLoaded(true);
        };
        sObj.src = sv.src || "";
      });
    });
    // Usunięto dodatkowe });
  }, [pages]);

  // Usunięto useEffect dla autosnapu - logika przeniesiona z powrotem do Editor.js

  // Obsługa guides:
  // Dodajemy zmienną do śledzenia ostatniego czasu dodania prowadnicy
  const lastGuideAddTimeRef = useRef(0);

  // Dodajemy licznik wywołań dla debugowania
  const addGuideCallCountRef = useRef(0);

  // Dodajemy referencję do ostatnio dodanej prowadnicy
  const lastAddedGuideRef = useRef({ timestamp: 0, id: null });

  function handleAddGuide({ orientation, expression }) {
    // Zwiększamy licznik wywołań
    const callCount = ++addGuideCallCountRef.current;
    console.log(`[EditorState] handleAddGuide wywołane (${callCount}): `, { orientation, expression });

    // Dodajemy stack trace, aby zobaczyć skąd jest wywoływana funkcja
    console.log(`[EditorState] Stack trace dla wywołania ${callCount}:`, new Error().stack);

    if (publicMode && isAccepted) {
      console.log(`[EditorState] Przerwano dodawanie prowadnicy (${callCount}) - tryb publiczny i zaakceptowane`);
      return;
    }

    // Sprawdzamy, czy nie próbujemy dodać prowadnicy zbyt szybko po poprzedniej
    const now = Date.now();
    if (now - lastGuideAddTimeRef.current < 500) {
      console.log(`[EditorState] Zablokowano dodanie prowadnicy (${callCount}) - zbyt szybko po poprzedniej (${now - lastGuideAddTimeRef.current}ms)`);
      return; // Blokujemy dodanie prowadnicy, jeśli minęło mniej niż 500ms
    }

    // Aktualizujemy czas ostatniego dodania prowadnicy
    lastGuideAddTimeRef.current = now;
    console.log(`[EditorState] Aktualizowano lastGuideAddTimeRef na ${now}`);

    // Generujemy unikalny identyfikator z większą precyzją
    const uniqueId = "guide-" + now + "-" + Math.floor(Math.random() * 1000);
    console.log(`[EditorState] Wygenerowano uniqueId: ${uniqueId}`);

    // Zapisujemy informacje o ostatnio dodanej prowadnicy
    lastAddedGuideRef.current = { timestamp: now, id: uniqueId };

    setPages((prev) => {
      console.log(`[EditorState] setPages wywołane dla prowadnicy (${callCount})`);

      // Sprawdzamy, czy ta sama aktualizacja nie jest wykonywana dwukrotnie
      // Porównujemy aktualną liczbę prowadnic z liczbą prowadnic w poprzednim stanie
      const currentGuides = prev[currentPage]?.guides || [];

      // Sprawdzamy, czy prowadnica o tym samym wyrażeniu i orientacji już istnieje
      const duplicateGuide = currentGuides.find(g =>
        g.orientation === orientation &&
        g.expression === expression &&
        // Sprawdzamy, czy prowadnica została dodana niedawno (w ciągu ostatnich 1000ms)
        g.id.startsWith(`guide-${Math.floor(now / 1000)}`)
      );

      if (duplicateGuide) {
        console.log(`[EditorState] Wykryto duplikat prowadnicy: ${duplicateGuide.id}, pomijam dodawanie`);
        return prev; // Zwracamy poprzedni stan bez zmian
      }

      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) {
        console.log(`[EditorState] Nieprawidłowy indeks strony: ${currentPage}`);
        return arr;
      }

      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];

      pCopy.guides.push({
        id: uniqueId,
        orientation,
        expression
      });

      console.log(`[EditorState] Dodano prowadnicę (${callCount}), aktualna liczba prowadnic: ${pCopy.guides.length}`);
      console.log(`[EditorState] Lista prowadnic po dodaniu:`, pCopy.guides);

      arr[currentPage] = pCopy;
      return arr;
    });

    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      console.log(`[EditorState] Wykonuję doAutoSave() po dodaniu prowadnicy (${callCount})`);
      doAutoSave();
      changesCountRef.current = 0;
    }

    console.log(`[EditorState] Zakończono handleAddGuide (${callCount})`);
  }
  function handleRemoveGuide(guideId) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.filter((g) => g.id !== guideId);
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }
  function handleChangeGuide(guideId, newExpression) {
    if (publicMode && isAccepted) return;
    setPages((prev) => {
      const arr = [...prev];
      if (currentPage < 0 || currentPage >= arr.length) return arr;
      const pCopy = { ...arr[currentPage] };
      if (!pCopy.guides) pCopy.guides = [];
      pCopy.guides = pCopy.guides.map((g) =>
        g.id === guideId ? { ...g, expression: newExpression } : g
      );
      arr[currentPage] = pCopy;
      return arr;
    });
    changesCountRef.current++;
    if (changesCountRef.current >= 5) {
      doAutoSave();
      changesCountRef.current = 0;
    }
  }

  return {
    // podst. tablice
    pages,
    setPages,
    currentPage,
    setCurrentPage,

    // wczytywanie stron
    hasLoadedFromDb,
    loadingPages,

    // fonty, assets
    fontsAreLoaded,
    availableFonts,
    assetsAreLoaded,
    assetsTotal,
    assetsCount,

    // zaznaczenia
    selectedShapeId,
    setSelectedShapeId,
    selectedObject,
    setSelectedObject,
    panelMode,
    setPanelMode,
    editingTextId,
    setEditingTextId,

    forceRefresh,
    doAutoSave,
    handleManualSave,
    changesCountRef,

    // public vs admin
    publicMode,
    publicMethodUsed,

    // guides
    handleAddGuide,
    handleRemoveGuide,
    handleChangeGuide,

    // Funkcja do manualnego wyzwalania odświeżenia
    triggerForceRefresh: () => setForceRefresh(p => p + 1)
    // Usunięto zwracanie autoSnapAllPagesWithSimulation
  };
}
